import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/ru";

import { EVENT_COLOR, type EventKind } from "@/lib/eventColors";
import type { Challenge, Tournament, Training } from "@/types/api";

dayjs.locale("ru");

export interface CalendarItem {
  kind: EventKind;
  id: number;
  date: Dayjs;
  /** Set if the current user is a creator/participant — renders a subtle ring. */
  mine: boolean;
  /** Source payload, so the parent can reuse its existing card renderers. */
  source: Tournament | Training | Challenge;
}

interface Props {
  items: CalendarItem[];
  /** Currently selected day, or null for today. Parent owns the state. */
  selected: Dayjs | null;
  onSelect: (day: Dayjs) => void;
}

/**
 * Compact month calendar tuned to the rest of the app's card look.
 *
 * Each day cell shows up to three coloured dots — one per event type
 * present that day. If any of those events involve the current user the
 * cell gets a soft ring so "мои события" stand out without pulling focus
 * from the filter colours.
 *
 * Kept renderless of event details on purpose: the parent owns the list
 * that expands for the selected day. This way the calendar just answers
 * "on which days is something happening" and doesn't fight with the card
 * components that already render the "what".
 */
export function EventCalendar({ items, selected, onSelect }: Props) {
  const [cursor, setCursor] = useState(() =>
    (selected ?? dayjs()).startOf("month"),
  );

  const activeDay = selected ?? dayjs();

  const byDay = useMemo(() => {
    const map = new Map<string, { kinds: Set<EventKind>; mine: boolean }>();
    for (const it of items) {
      const key = it.date.format("YYYY-MM-DD");
      const slot = map.get(key) ?? { kinds: new Set<EventKind>(), mine: false };
      slot.kinds.add(it.kind);
      slot.mine = slot.mine || it.mine;
      map.set(key, slot);
    }
    return map;
  }, [items]);

  // Build a 6-row grid, Monday-first (ru locale). We always show six rows
  // so the layout doesn't jump when switching months.
  const cells = useMemo(() => {
    const first = cursor.startOf("month");
    // ru locale weekday: Mon=1 … Sun=7. Shift so grid starts on Monday.
    const lead = (first.day() + 6) % 7;
    const start = first.subtract(lead, "day");
    const out: Dayjs[] = [];
    for (let i = 0; i < 42; i++) out.push(start.add(i, "day"));
    return out;
  }, [cursor]);

  const weekdayLabels = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

  return (
    <div
      style={{
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
        margin: "0 16px 12px",
        padding: 12,
        fontFamily: "var(--font-body)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <button
          type="button"
          aria-label="Предыдущий месяц"
          onClick={() => setCursor((c) => c.subtract(1, "month"))}
          style={navBtnStyle}
        >
          <ChevronLeft size={18} />
        </button>
        <div style={{ flex: 1, fontWeight: 600, fontSize: 15, textAlign: "center" }}>
          {cursor.format("MMMM YYYY")}
        </div>
        <button
          type="button"
          aria-label="Следующий месяц"
          onClick={() => setCursor((c) => c.add(1, "month"))}
          style={navBtnStyle}
        >
          <ChevronRight size={18} />
        </button>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
          fontSize: 11,
          color: "var(--fg3)",
          marginBottom: 4,
        }}
      >
        {weekdayLabels.map((d) => (
          <div key={d} style={{ textAlign: "center", padding: "2px 0" }}>
            {d}
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
        }}
      >
        {cells.map((d) => {
          const key = d.format("YYYY-MM-DD");
          const info = byDay.get(key);
          const inMonth = d.month() === cursor.month();
          const isToday = d.isSame(dayjs(), "day");
          const isSelected = d.isSame(activeDay, "day");
          const kinds = info ? Array.from(info.kinds) : [];

          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(d)}
              aria-pressed={isSelected}
              style={{
                position: "relative",
                aspectRatio: "1 / 1",
                border: 0,
                borderRadius: "var(--r-md)",
                padding: 4,
                cursor: "pointer",
                background: isSelected
                  ? "var(--brand-ink)"
                  : buildCellTint(kinds),
                color: isSelected
                  ? "#fff"
                  : inMonth
                    ? "var(--fg1)"
                    : "var(--fg3)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                fontWeight: isToday ? 700 : 400,
                fontSize: 13,
                outline: info?.mine && !isSelected
                  ? "1.5px solid var(--brand-orange)"
                  : "none",
                outlineOffset: -1,
              }}
            >
              <span style={{ marginTop: 2 }}>{d.date()}</span>
              {kinds.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: 3,
                    marginTop: "auto",
                    marginBottom: 3,
                  }}
                >
                  {kinds.map((k) => (
                    <span
                      key={k}
                      aria-hidden
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        background: isSelected ? "#fff" : EVENT_COLOR[k],
                      }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Blend the tints of the event types active on a day so a day with only
 * tournaments leans orange, one with only trainings leans green, etc.
 * Multi-type days land on a muted mix rather than any one accent colour.
 */
function buildCellTint(kinds: EventKind[]): string {
  if (kinds.length === 0) return "var(--bg)";
  if (kinds.length === 1) {
    const k = kinds[0];
    // Same rgba stops used for chip idle state — kept in sync here
    // explicitly so the calendar reads as "related to the filter row".
    if (k === "tournament") return "rgba(255,90,31,0.10)";
    if (k === "training") return "rgba(30,122,90,0.12)";
    return "rgba(139,92,246,0.12)";
  }
  return "rgba(16,19,26,0.04)";
}

const navBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  borderRadius: "var(--r-md)",
  width: 32,
  height: 32,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "var(--fg2)",
};
